import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, IconPicker, Spinner, useTheme, useToast } from "@aigntx/ui";
import type { TenantTheme } from "@aigntx/ui";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import { useTenantPatch } from "../tenant";
import { BrandMark } from "../BrandMark";
import type { TenantConfig } from "../types";

type BrandKey = "primary" | "accent1" | "accent2" | "warn" | "danger";
const BRAND_KEYS: BrandKey[] = ["primary", "accent1", "accent2", "warn", "danger"];

/** Brand-colour editor with live preview — embedded in the Company page. */
export function BrandingEditor() {
  const { t } = useTranslation();
  const { setTheme } = useTheme();
  const { can } = useAuth();
  const toast = useToast();
  const patchTenant = useTenantPatch();
  const canManage = can("tenant.manage");
  const [profile, setProfile] = useState<TenantConfig | null>(null);
  const [colors, setColors] = useState<Record<BrandKey, string> | null>(null);
  const [icon, setIcon] = useState<string>("◆");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    endpoints
      .getTenantProfile()
      .then((p) => {
        setProfile(p);
        setIcon(String(p.theme.icon ?? "◆"));
        setColors({
          primary: p.theme.primary,
          accent1: p.theme.accent1,
          accent2: p.theme.accent2,
          warn: p.theme.warn,
          danger: p.theme.danger,
        });
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  function setColor(key: BrandKey, value: string) {
    setColors((c) => (c ? { ...c, [key]: value } : c));
    setTheme({ [key]: value } as Partial<TenantTheme>); // live preview across the app
  }

  async function save() {
    if (!colors) return;
    setBusy(true);
    setError("");
    try {
      setProfile(await endpoints.updateTenantProfile({ theme: { ...colors, icon } }));
      patchTenant({ theme: { ...colors, icon } }); // apply to the shell live
      setSaved(true);
      toast.success(t("branding.saved"));
      window.setTimeout(() => setSaved(false), 1600);
    } catch (e) {
      const text = e instanceof Error ? e.message : String(e);
      setError(text);
      toast.error(text);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    if (!profile) return;
    const c: Record<BrandKey, string> = {
      primary: profile.theme.primary,
      accent1: profile.theme.accent1,
      accent2: profile.theme.accent2,
      warn: profile.theme.warn,
      danger: profile.theme.danger,
    };
    setColors(c);
    setTheme(c as Partial<TenantTheme>);
  }

  if (error && !profile) return <Card>{error}</Card>;
  if (!colors)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  return (
    <div className="brand-grid">
      <Card title={t("branding.identity")}>
        <div className="swatches">
          {BRAND_KEYS.map((k) => (
            <label className="swatch" key={k}>
              <input
                type="color"
                value={colors[k]}
                onChange={(e) => setColor(k, e.target.value)}
                aria-label={t(`branding.${k}`)}
                disabled={!canManage}
              />
              <span className="swatch__label">{t(`branding.${k}`)}</span>
              <span className="swatch__hex mono">{colors[k]}</span>
            </label>
          ))}
        </div>
        <div className="brand-icon-field">
          <div className="brand-icon-head">
            <span className="settings__label">{t("branding.icon")}</span>
            <BrandMark icon={icon} size={40} />
          </div>
          <IconPicker value={icon} onChange={setIcon} />
        </div>
        {error && <div className="form-err">{error}</div>}
        {canManage && (
          <div className="brand-actions">
            <Button variant="ghost" onClick={reset}>
              {t("branding.reset")}
            </Button>
            <Button loading={busy} onClick={() => void save()}>
              {saved ? t("settings.saved") : t("common.save")}
            </Button>
          </div>
        )}
      </Card>

      <Card title={t("branding.preview")}>
        <p className="page__sub" style={{ marginBottom: "var(--space-4)" }}>
          {t("branding.previewHint")}
        </p>
        <div className="preview-row">
          <Button>{t("common.save")}</Button>
          <Button variant="outline">{t("branding.reset")}</Button>
          <Button variant="danger">{t("users.off")}</Button>
        </div>
        <div className="preview-row">
          <Badge tone="good">good</Badge>
          <Badge tone="warn">warn</Badge>
          <Badge tone="danger">danger</Badge>
          <Badge tone="accent">accent</Badge>
        </div>
        <div className="preview-bar">
          <span style={{ width: "68%" }} />
        </div>
      </Card>
    </div>
  );
}
